/**
 * RiderSettlementHistory Helpers
 * 라이더 주정산내역 헬퍼 함수 및 상수
 */
import {
    TableColumn,
    FilterValidation,
    shouldShowColumn as baseShouldShowColumn,
    getColumnAlignment as baseGetColumnAlignment,
    validateDateRange,
    isValidExcelFile
} from '../../../shared/lib/settlementBaseHelpers';

// 타입 재내보내기
export type { TableColumn, FilterValidation };

// 함수 재내보내기
export { isValidExcelFile };

/**
 * 금액 포맷팅 함수 (null/undefined 안전 처리)
 * @param value - 포맷팅할 금액
 * @returns 포맷팅된 금액 문자열
 */
export const formatCurrency = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('ko-KR').format(value);
};

/**
 * 테이블 컬럼 목록
 */
export const TABLE_COLUMNS: TableColumn[] = [
    { index: 1, header: 'NO', width: '60px', minWidth: '60px' },
    { index: 2, header: '라이더ID', width: '120px', minWidth: '120px' },
    { index: 3, header: '라이더명', width: '100px', minWidth: '100px' },
    { index: 4, header: '라이더구분', width: '100px', minWidth: '100px', tooltip: '만65세이상, 지점장, 일반' },
    { index: 5, header: '정산시작일', width: '120px', minWidth: '120px' },
    { index: 6, header: '정산종료일', width: '120px', minWidth: '120px' },
    { index: 7, header: '처리건수', width: '100px', minWidth: '100px' },
    { index: 8, header: '배달료', width: '120px', minWidth: '120px' },
    { index: 9, header: '추가지급', width: '120px', minWidth: '120px' },
    { index: 10, header: '중개사수수료', width: '140px', minWidth: '140px', tooltip: '부릉라이더스에서 설정한 라이더 수취 수수료\n(건당 금액의 합계)' },
    { index: 11, header: '협력사수수료', width: '140px', minWidth: '140px', tooltip: '협력사에서 설정한 라이더 수취 수수료\n(건당 금액의 합계)' },
    { index: 12, header: '총운영수수료', width: '140px', minWidth: '140px', tooltip: '중개사수수료 + 협력사수수료' },
    { index: 13, header: '과세배달료', width: '120px', minWidth: '120px', tooltip: '배달료 + 추가지급 - 총운영수수료\n(고용산재 부과 대상 금액)' },
    { index: 14, header: '월보수액', width: '120px', minWidth: '120px', tooltip: '과세배달료 × 80.2% (반올림)\n※ 1~2년마다 수식 변경 가능' },
    { index: 15, header: '라이더_고용', width: '120px', minWidth: '120px', tooltip: '월보수액 × 0.8% (반올림)\n※ 지점장, 만65세이상은 0원' },
    { index: 16, header: '라이더_산재', width: '120px', minWidth: '120px', tooltip: '월보수액 × 0.88% (반올림)\n※ 지점장만 0원' },
    { index: 17, header: '시간제보험료', width: '120px', minWidth: '120px' },
    { index: 18, header: '총합', width: '100px', minWidth: '100px', tooltip: '라이더_고용 + 라이더_산재 + 시간제보험료' },
    { index: 19, header: '프로모션', width: '120px', minWidth: '120px', tooltip: '비정기적 지급 항목\n(원천세만 차감)' },
    { index: 20, header: '소득세', width: '100px', minWidth: '100px', tooltip: '과세배달료 × 3% (반올림)' },
    { index: 21, header: '지방세', width: '100px', minWidth: '100px', tooltip: '소득세 × 10% (반올림)' },
    { index: 22, header: '원천징수', width: '120px', minWidth: '120px', tooltip: '소득세 + 지방세' },
    { index: 23, header: '최종배달료', width: '120px', minWidth: '120px', tooltip: '과세배달료 - 총합 + 프로모션 - 원천징수' },
    { index: 24, header: '선정산배달료', width: '140px', minWidth: '140px' },
    { index: 25, header: '일차감', width: '100px', minWidth: '100px', tooltip: '라이더가 협력사에게 전달하는 금액' },
    { index: 26, header: '실지급배달료', width: '140px', minWidth: '140px', tooltip: '최종배달료 - 선정산배달료 - 일차감' },
];

/** 라이더 사용자에게 표시되는 컬럼 인덱스 목록 */
export const RIDER_VISIBLE_COLUMNS = [2, 3, 4, 5, 6, 7, 8, 9, 10, 13, 14, 16, 17, 18, 20, 21, 22, 24, 25, 26] as const;

/** 금액 컬럼 인덱스 (우측 정렬) */
export const CURRENCY_COLUMNS = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26] as const;

/** 중앙 정렬 컬럼 인덱스 */
export const CENTER_ALIGN_COLUMNS = [1, 2, 4, 5, 6, 7] as const;

/**
 * 권한별 컬럼 표시 여부 확인
 */
export const shouldShowColumn = (columnIndex: number, isRiderUser: boolean): boolean => {
    return baseShouldShowColumn(columnIndex, RIDER_VISIBLE_COLUMNS, isRiderUser);
};

/**
 * 컬럼 정렬 스타일 반환
 */
export const getColumnAlignment = (columnIndex: number): React.CSSProperties => {
    return baseGetColumnAlignment(columnIndex, CENTER_ALIGN_COLUMNS, CURRENCY_COLUMNS);
};

/**
 * 필터 유효성 검사 (라이더 사용자 여부에 따라 다른 검증)
 */
export const validateFilters = (
    acctStDate: string,
    acctEdDate: string,
    _supplierId: string,
    _isRiderUser: boolean
): FilterValidation => {
    return validateDateRange(acctStDate, acctEdDate);
};

/**
 * 초기 날짜 범위 반환 (API에서 받은 시작일/종료일 그대로 사용)
 */
export const getInitialDateRange = (startDate: string, endDate: string): { startDate: string; endDate: string } => {
    return { startDate, endDate };
};
